import { useEffect } from 'react';

/**
 * Vendor-script helpers that ApartmentSync (Repli360 / RRAC) appends
 * directly to <body> (modal/popup shell, loader spinner, date picker).
 * They live outside our React-controlled container, so React's unmount
 * can't reach them. Hide-via-CSS left the nodes in the document; cleanup
 * should drop them instead.
 *
 * Floor Plans (`apartment-sync`) and Interactive Map (`apartment-sync-map`)
 * each mount their own widget, but they share one vendor script and the
 * same body-level class names — they are not namespaced per tab. A
 * module-level refcount + delay lets an incoming tab cancel the outgoing
 * tab's cleanup so we don't delete shells the new widget still needs
 * (matches core's useRRACScript consumer handoff).
 */
const RRAC_BODY_HELPER_SELECTORS = ['.rrac-popup', '.rrac_modal_container'];
const CLEANUP_DELAY_MS = 100;

let mountCount = 0;
let cleanupTimer: number | null = null;

function removeRRACBodyHelpers(): void {
  for (const sel of RRAC_BODY_HELPER_SELECTORS) {
    document.querySelectorAll(sel).forEach((el) => {
      if (el.parentElement === document.body) el.remove();
    });
  }
}

export function useCleanupRRACBodyHelpers(): void {
  useEffect(() => {
    mountCount += 1;
    if (cleanupTimer !== null) {
      window.clearTimeout(cleanupTimer);
      cleanupTimer = null;
    }

    return () => {
      mountCount = Math.max(0, mountCount - 1);
      if (mountCount > 0 || cleanupTimer !== null) return;
      cleanupTimer = window.setTimeout(() => {
        cleanupTimer = null;
        if (mountCount > 0) return;
        removeRRACBodyHelpers();
      }, CLEANUP_DELAY_MS);
    };
  }, []);
}
